import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useOverlay from '@/hooks/useOverlay'
import { ApprovalStatePolicy } from '@/types/approvals'
import { AuthUser } from '@/types/users'
import {
  ApprovalColumn,
  ApprovalScreen,
  ApprovalTableRow,
  columnVisibility,
} from '@/utils/approval-columns'
import ApprovalPolicyDetailsDialog from '@/components/approvals/ApprovalPolicyDetailsDialog'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'
import useApprovalColumns from './useApprovalColumns'

const SKELETON_ROWS = 1

type ApprovalTableProps = {
  columns: ApprovalColumn[]
  screen: ApprovalScreen
  rows: ApprovalTableRow[]
  hasMore: boolean
  isLoading: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
  onOpenDocument: (documentId: string) => void
  emptyMessage: string
  usersById: Map<string, AuthUser>
  usersPending: boolean
}

export default function ApprovalTable({
  columns,
  screen,
  rows,
  hasMore,
  isLoading,
  isFetchingMore,
  onLoadMore,
  onOpenDocument,
  emptyMessage,
  usersById,
  usersPending,
}: ApprovalTableProps) {
  const getThemeColor = useGetThemeColor()

  const bottomRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const bottom = bottomRef.current
    if (!bottom || !hasMore || isLoading || isFetchingMore) return
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      onLoadMore()
    })
    observer.observe(bottom)
    return () => observer.disconnect()
  }, [hasMore, isLoading, isFetchingMore, onLoadMore, rows.length])

  // One dialog for the whole table rather than one per row, so a click inside
  // it can't bubble up to the row that opens the document.
  const policyOverlay = useOverlay()
  const [openPolicy, setOpenPolicy] = useState<ApprovalStatePolicy>()
  const openPolicyDetails = useCallback(
    (policy: ApprovalStatePolicy) => {
      setOpenPolicy(policy)
      policyOverlay.open()
    },
    [policyOverlay],
  )

  const tableColumns = useApprovalColumns({
    columns,
    usersById,
    usersPending,
    onOpenPolicy: openPolicyDetails,
  })

  const table = useZerapixTable<ApprovalTableRow>({
    columns: tableColumns,
    data: rows,
    getRowId: (row) => row.id,
    enableTopToolbar: false,
    enableBottomToolbar: !isLoading && isFetchingMore,
    // Pagination is off, but this is still how many skeleton rows show while loading.
    initialState: { pagination: { pageIndex: 0, pageSize: SKELETON_ROWS } },
    state: {
      columnVisibility: columnVisibility(columns, screen),
      showSkeletons: isLoading,
    },
    muiTablePaperProps: { variant: 'outlined', elevation: 0 },
    muiTableHeadRowProps: {
      sx: {
        boxShadow: 'none',
        // Lines the header up with the rows, which carry a color stripe here.
        // It takes the head cells' tint rather than `transparent`, which would
        // show the paper underneath as a sliver beside the first header cell.
        borderLeft: 3,
        borderLeftColor: 'action.hover',
        borderLeftStyle: 'solid',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: (theme) => `${theme.palette.action.hover} !important`,
        px: 1.25,
        py: 1.5,
        fontSize: (theme) => theme.typography.caption.fontSize,
        fontWeight: 600,
        color: 'text.secondary',
        '&:first-of-type': { pl: 2.5 },
        '&:last-of-type': { pr: 2.5 },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const open = () => onOpenDocument(row.original.documentId)
      return {
        tabIndex: 0,
        'aria-label': 'Open document',
        onClick: open,
        onKeyDown: (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            open()
          }
        },
        sx: {
          minHeight: 54,
          borderLeft: 3,
          borderLeftColor: getThemeColor(
            row.original.detail?.workflowState?.color,
          ),
          borderLeftStyle: 'solid',
          cursor: 'pointer',
        },
      }
    },
    muiTableBodyCellProps: {
      sx: {
        px: 1.25,
        py: 1,
        whiteSpace: 'normal',
        '&:first-of-type': { pl: 2.5 },
        '&:last-of-type': { pr: 2.5 },
      },
    },
    renderEmptyRowsFallback: () =>
      hasMore ? null : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2.5, py: 4, textAlign: 'center' }}
        >
          {emptyMessage}
        </Typography>
      ),
    muiBottomToolbarProps: {
      sx: {
        boxShadow: 'none',
        borderTop: 1,
        borderColor: 'divider',
      },
    },
    renderBottomToolbarCustomActions: () => (
      <Stack alignItems="center" sx={{ width: '100%', py: 1 }}>
        <CircularProgress size={20} aria-label="Loading more" />
      </Stack>
    ),
  })

  return (
    <>
      <ZerapixTable<ApprovalTableRow> table={table} />
      <Box ref={bottomRef} />

      {openPolicy && (
        <ApprovalPolicyDetailsDialog
          overlay={policyOverlay}
          policy={openPolicy}
          usersById={usersById}
          usersPending={usersPending}
        />
      )}
    </>
  )
}
